import { Button } from '@/components/ui/button'
import { cryptoCurrencyDataSource } from '@/constants/datasource'
import { fiatCurrencies } from '@/constants/fiatCurrency'
import { useToast } from '@/hooks/useToast'
import { cn } from '@/lib/utils'
import type { PortfolioPageData } from '@/types'
import { showErrorToast } from '@/utils/error'
import {
  calculateTransactionPlByWacb,
  createAssetBalancesForWacb,
  createTransactions,
} from '@0xtorch/accounting'
import { generateAccountingTransactionList } from '@pkg/analyze'
import { MixIcon } from '@radix-ui/react-icons'
import { useAtom } from 'jotai'
import { Loader2 } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  isRequiredCalculationAtom,
  updateIsRequiredCalculationAtom,
} from '../../states/calculate'
import {
  calculationProgressAtom,
  exchangeApiProgressesAtom,
  progressesAtom,
} from '../../states/progress'
import type { CalculationProgress, ExchangeApiProgress } from '../../types'

const UpdatingTransactionToast = (
  props: Pick<
    PortfolioPageData,
    'readFromDatabase' | 'writeToDatabase' | 'readonly'
  >,
) => {
  const { readonly, readFromDatabase, writeToDatabase } = props
  const { t } = useTranslation()
  const { toast } = useToast()

  const [progresses] = useAtom(progressesAtom)
  const [exchangeApiProgressRecord] = useAtom(exchangeApiProgressesAtom)
  const exchangeApiProgress = Object.values(exchangeApiProgressRecord).filter(
    (progress): progress is ExchangeApiProgress => progress !== undefined,
  )
  const [calculationProgress, setCalculationProgress] = useAtom(
    calculationProgressAtom,
  )
  const [isRequiredCalculation] = useAtom(isRequiredCalculationAtom)
  const [, updateIsRequiredCalculation] = useAtom(
    updateIsRequiredCalculationAtom,
  )
  const calculate = async () => {
    if (!isRequiredCalculation) {
      return
    }
    if (writeToDatabase === undefined) {
      return
    }
    setCalculationProgress({
      size: 1,
      done: 0,
      range: 1,
      synced: 0,
    })
    try {
      await generateAccountingTransactionList({
        readFromDatabase,
        writeToDatabase,
        fiats: fiatCurrencies,
        cryptoCurrencyDataSource,
        createTransactions: (args) => Promise.resolve(createTransactions(args)),
        createAssetBalancesForWacb: (args) =>
          Promise.resolve(createAssetBalancesForWacb(args)),
        calculateTransactionPlByWacb: (args) =>
          Promise.resolve(calculateTransactionPlByWacb(args)),
        onUpdateProgress: (change) => {
          setCalculationProgress((prev) =>
            prev === undefined
              ? {
                  size: 1,
                  done: 0,
                  range: 1,
                  synced: 0,
                  ...change,
                }
              : {
                  ...prev,
                  ...change,
                },
          )
        },
      })
      await updateIsRequiredCalculation({ readFromDatabase })
    } catch (error) {
      showErrorToast({
        error,
        t,
        toast,
      })
    } finally {
      setCalculationProgress(undefined)
    }
  }

  return (
    <UI
      isHidden={
        readonly ||
        progresses.length > 0 ||
        exchangeApiProgress.length > 0 ||
        !isRequiredCalculation
      }
      progress={calculationProgress}
      onCalculate={calculate}
    />
  )
}

const UI = ({
  isHidden,
  progress,
  onCalculate,
}: {
  readonly isHidden: boolean
  readonly progress: CalculationProgress | undefined
  readonly onCalculate: () => void
}) => {
  const { t } = useTranslation()

  const percent = useMemo(
    () =>
      progress === undefined
        ? 0
        : Math.max(
            (progress.synced / Math.max(progress.range, progress.synced, 1) +
              progress.done) /
              progress.size -
              0.01,
            0,
          ) * 100,
    [progress],
  )

  return (
    <div
      className={cn(
        'absolute top-1 right-0 left-0 z-50 mx-auto inline-block w-80 duration-500',
        isHidden && 'translate-y-[-64px]',
      )}
    >
      <div className="flex w-full items-center justify-between space-x-4 rounded-full border border-primary/75 bg-background px-4 py-2 shadow">
        <p className="font-semibold text-muted-foreground text-sm">
          {t('Profit/Loss calculation is incompleted')}
        </p>
        <Button
          size="sm"
          disabled={progress !== undefined}
          onClick={onCalculate}
          className="h-auto rounded-full py-1.5"
        >
          {progress === undefined ? (
            <MixIcon />
          ) : (
            <Loader2 className="animate-spin" />
          )}
          <span>
            {progress === undefined
              ? t('toast/update-transaction/button/calculate')
              : `${Math.floor(percent)} %`}
          </span>
        </Button>
      </div>
    </div>
  )
}

export default UpdatingTransactionToast
